import type { estypes } from '@elastic/elasticsearch'
import { z } from 'zod'

import type { Via } from '@slite/shared'

import type { GPTFunction } from './index'
import type {
  NonArray,
  SearchHitWithRequiredFields,
  SearchObjectSuperFragment,
  SearchObjectSuperGithubFragment,
  SearchObjectSuperJiraFragment,
  SearchObjectSuperLinearFragment,
  SearchObjectSuperNotionFragment,
  SearchObjectSuperSliteFragment,
} from './mapping'
import type { Effort } from './model-profiles'
import type { SearchParentsHit } from './search'
import type { GenericMapping } from './search/types'

export const MAGIC_SOURCE_PREFIX = '5L1T3-'
export const MAGIC_ARTIFACT_PREFIX = '4RT1F4CT-'

/** Closes a result's cited list: what follows describes the result set, not the
 *  last source. Distinctive because a bare `---` occurs inside document bodies. */
export const END_OF_SOURCES = '--- end of sources ---'

type Prefixed<T, P extends string | undefined = undefined> = {
  [K in Extract<keyof T, string> as P extends string ? `${P}.${K}` : K]: T[K]
}

export type SuperMedia = {
  url: string
  caption?: string
  sourceId?: string
}

export enum SuperSource {
  GOOGLE_DRIVE = 'Google Drive',
  MICROSOFT_SHAREPOINT = 'Microsoft SharePoint',
  SLACK_CHANNEL = 'Slack Channel',
  LINEAR = 'Linear',
  NOTION = 'Notion',
  WEBSITE = 'Website',
  SLITE = 'Slite',
  FILE = 'File',
  CONFLUENCE = 'Confluence',
  GITHUB = 'GitHub',
  GITLAB = 'GitLab',
  JIRA = 'Jira',
  GIT = 'Git',
  CUSTOM = 'Custom',
  HUBSPOT = 'Hubspot',
  ATTIO = 'Attio',
  ASANA = 'Asana',
  INTERCOM = 'Intercom',
  BIGQUERY = 'BigQuery',
  MCP = 'MCP',
  SALESFORCE = 'Salesforce',
  ZOOM = 'Zoom',
  ZENDESK = 'Zendesk',
  MONDAY = 'Monday',
  MIRO = 'Miro',
  GRANOLA = 'Granola',
  AIRTABLE = 'Airtable',
  CLICKUP = 'ClickUp',
  AMPLITUDE = 'Amplitude',
  BOX = 'Box',
  SNOWFLAKE = 'Snowflake',
  NOTION_MCP = 'Notion MCP',
}

const SUPER_SOURCE_VALUES: ReadonlySet<string> = new Set(
  Object.values(SuperSource)
)

export function isSuperSource(value: string): value is SuperSource {
  return SUPER_SOURCE_VALUES.has(value)
}

/** First-class sources backed by a hand-picked MCP server. */
export const MCP_BACKED_SUPER_SOURCES = Object.freeze([
  SuperSource.MONDAY,
  SuperSource.MIRO,
  SuperSource.GRANOLA,
  SuperSource.AIRTABLE,
  SuperSource.CLICKUP,
  SuperSource.AMPLITUDE,
  SuperSource.BOX,
  SuperSource.SNOWFLAKE,
  SuperSource.NOTION_MCP,
])

/**
 * Sources whose fragments are MCP tool entries: the custom server source
 * plus the MCP-backed ones.
 */
export const MCP_TOOL_SUPER_SOURCES = Object.freeze([
  SuperSource.MCP,
  ...MCP_BACKED_SUPER_SOURCES,
])

export enum SuperRootNodeType {
  SLITE_ORGANIZATION = 'SliteOrganization',
  SLITE_ORGANIZATION_REINDEXED = 'SliteOrganizationReindexed',
  LINEAR_TEAM = 'LinearTeam',
  ATTIO_LIST = 'AttioList',
  ATTIO_OBJECT = 'AttioObject',
  ATTIO_MEETINGS = 'AttioMeetings',
  ASANA_PROJECT = 'AsanaProject',
  GITHUB_REPO = 'GithubRepo',
  GITLAB_PROJECT = 'GitlabProject',
  JIRA_PROJECT = 'JiraProject',
  CONFLUENCE_SPACE = 'ConfluenceSpace',
  NOTION_WORKSPACE = 'NotionWorkspace',
  GIT_REPO = 'GitRepo',
  FILE = 'File',
  CUSTOM = 'Custom',
  WEBSITE = 'Website',
  SLACK_PUBLIC_CHANNEL = 'SlackPublicChannel',
  SLACK_PRIVATE_CHANNEL = 'SlackPrivateChannel',
  SLACK_CHANNEL_FROM_ZIP = 'SlackChannelFromZip',
  HUBSPOT_CRM = 'HubspotCRM',
  GOOGLE_DRIVE_FOLDER = 'GoogleDriveFolder',
  BIGQUERY_TABLE = 'BigQueryTable',
  INTERCOM_WORKSPACE = 'IntercomWorkspace',
  MCP_SERVER = 'MCPServer',
  SHAREPOINT_SITE = 'SharepointSite',
  SALESFORCE = 'Salesforce',
  ZOOM = 'Zoom',
  ZENDESK = 'Zendesk',
}

export enum SuperConnectionError {
  GITHUB_INVALID_AUTHENTICATION = 'GithubInvalidAuthentication',
  GITLAB_INVALID_AUTHENTICATION = 'GitlabInvalidAuthentication',
  NOTION_INVALID_AUTHENTICATION = 'NotionInvalidAuthentication',
  LINEAR_INVALID_AUTHENTICATION = 'LinearInvalidAuthentication',
  SLACK_ACCOUNT_INACTIVE = 'SlackAccountInactive',
  SLACK_MISSING_SCOPE = 'SlackMissingScope',
  SLACK_MISSING_CLIENT = 'SlackMissingClient',
  RATE_LIMIT_EXCEEDED = 'RateLimitExceeded',
  HUBSPOT_MISSING_SCOPE = 'HubspotMissingScope',
  HUBSPOT_BAD_REFRESH_TOKEN = 'HubspotBadRefreshToken',
  HUBSPOT_BAD_HUB = 'HubspotBadHub',
  HUBSPOT_INVALID_AUTHENTICATION = 'HubspotInvalidAuthentication',
  GIT_INVALID_CREDENTIALS = 'GitInvalidCredentials',
  GOOGLE_DRIVE_INVALID_SESSION = 'GoogleDriveInvalidSession',
  GOOGLE_DRIVE_INVALID_CREDENTIALS = 'GoogleDriveInvalidCredentials',
  GOOGLE_DRIVE_APPS_DISABLED = 'GoogleDriveAppsDisabled',
  GOOGLE_DRIVE_INVALID_EMAIL = 'GoogleDriveInvalidEmail',
  GOOGLE_DRIVE_INVALID_DOMAIN = 'GoogleDriveInvalidDomain',
  SHAREPOINT_INVALID_AUTHENTICATION = 'SharepointInvalidAuthentication',
  PENDING_FORGE_INSTALLATION = 'PendingForgeInstallation',
  JIRA_UNAUTHORIZED = 'JiraUnauthorized',
  CONFLUENCE_UNAUTHORIZED = 'ConfluenceUnauthorized',
  JIRA_FAILED_DEPENDENCY = 'JiraFailedDependency',
  CONFLUENCE_FAILED_DEPENDENCY = 'ConfluenceFailedDependency',
  BIGQUERY_UNAUTHORIZED_CLIENT = 'BigQueryUnauthorizedClient',
  WEBSITE_DNS_RESOLUTION_FAILED = 'WebsiteDNSResolutionFailed',
  WEBSITE_INVALID_EXCLUDE_PATHS = 'WebsiteInvalidExcludePaths',
  ASANA_TOKEN_EXPIRED = 'AsanaTokenExpired',
  ASANA_UNAUTHORIZED = 'AsanaUnauthorized',
  ASANA_EXPORT_FORBIDDEN = 'AsanaExportForbidden',
  ZOOM_INVALID_AUTHENTICATION = 'ZoomInvalidAuthentication',
  ZOOM_APP_DISABLED = 'ZoomAppDisabled',
  ZENDESK_INVALID_AUTHENTICATION = 'ZendeskInvalidAuthentication',
  INTERCOM_FORBIDDEN = 'IntercomForbidden',
  MCP_INVALID_AUTHENTICATION = 'McpInvalidAuthentication',
  MCP_INSUFFICIENT_SCOPE = 'McpInsufficientScope',
  MCP_SERVER_NOT_FOUND = 'McpServerNotFound',
}

export enum SuperRootNodeError {
  GITHUB_MISSING_REPOSITORY = 'GithubMissingRepository',
  GITLAB_MISSING_REPOSITORY = 'GitlabMissingRepository',
  SLACK_BOT_NOT_IN_THE_CHANNEL = 'SlackBotNotInTheChannel',
  SLACK_CHANNEL_NOT_FOUND = 'SlackChannelNotFound',
  SLACK_BOT_ONLY_CHANNEL_DETECTED = 'SlackBotOnlyChannelDetected',
  SLACK_CHANNEL_ARCHIVED = 'SlackChannelArchived',
  GOOGLE_DRIVE_FOLDER_ALREADY_INDEXED_BY_OTHER_ROOT_NODE = 'GoogleDriveFolderAlreadyIndexedByOtherRootNode',
  GOOGLE_DRIVE_FOLDER_NOT_FOUND = 'GoogleDriveFolderNotFound',
  GOOGLE_DRIVE_FOLDER_CREATOR_NOT_FOUND = 'GoogleDriveFolderCreatorNotFound',
  SHAREPOINT_SITE_NOT_FOUND = 'SharepointSiteNotFound',
  SHAREPOINT_SITE_LIST_NOT_FOUND = 'SharepointSiteListNotFound',
  GITHUB_INVALID_REPOSITORY = 'GithubInvalidRepository',
  ASANA_PROJECT_ACCESS = 'AsanaProjectAccess',
  ASANA_PROJECT_NOT_FOUND = 'AsanaProjectNotFound',
  JIRA_PROJECT_NOT_FOUND = 'JiraProjectNotFound',
  ATTIO_LIST_NOT_FOUND = 'AttioListNotFound',
  ATTIO_OBJECT_NOT_FOUND = 'AttioObjectNotFound',
}

export enum SuperIndexableNodeError {
  NOTION_UNSUPPORTED_BLOCK = 'NotionUnsupportedBlock',
  HUBSPOT_INVALID_OWNER_ID = 'HubspotInvalidOwnerId',
  HUBSPOT_HTML_TOO_BIG = 'HubspotHtmlTooBig',
  TOO_LARGE = 'TooLarge',
  GOOGLE_DRIVE_FILE_TOO_LARGE = 'GoogleDriveFileTooLarge',
  GOOGLE_DRIVE_MALWARE_DETECTED = 'GoogleDriveMalwareDetected',
  GOOGLE_DRIVE_CANNOT_EXPORT = 'GoogleDriveCannotExport',
  GOOGLE_DRIVE_PASSWORD_PROTECTED = 'GoogleDrivePasswordProtected',
  GOOGLE_DRIVE_CORRUPTED_FILE = 'GoogleDriveCorruptedFile',
  SHAREPOINT_FILE_TOO_LARGE = 'SharepointFileTooLarge',
  SHAREPOINT_CORRUPTED_FILE = 'SharepointCorruptedFile',
  INTERCOM_SIDE_CONVERSATION = 'IntercomSideConversation',
  SLITE_INVALID_PDF = 'SliteInvalidPdf',
  SLITE_DOC_SIZE_TOO_BIG = 'SliteDocSizeTooBig',
  SLITE_HTML_TOO_BIG = 'SliteHtmlTooBig',
  SLITE_DOC_CORRUPTED = 'SliteDocCorrupted',
  SLITE_MEDIA_TOO_BIG = 'SliteMediaTooBig',
  SLITE_DOCX_CORRUPTED = 'SliteDocxCorrupted',
  SLITE_PPTX_CORRUPTED = 'SlitePptxCorrupted',
  INTERCOM_HTML_TOO_BIG = 'IntercomHtmlTooBig',
}

export enum SuperIndexableNodeType {
  SLITE_NOTE = 'SliteNote',
  SLITE_NOTE_REINDEXED = 'SliteNoteReindexed',
  SLITE_MEDIA = 'SliteMedia',
  ASANA_TASK = 'AsanaTask',
  ATTIO_ENTRY = 'AttioEntry',
  ATTIO_RECORD = 'AttioRecord',
  ATTIO_TRANSCRIPT = 'AttioTranscript',
  GITHUB_ISSUE = 'GithubIssue',
  GITHUB_PR = 'GithubPR',
  GITLAB_ISSUE = 'GitlabIssue',
  GITLAB_MR = 'GitlabMR',
  GOOGLE_DRIVE_FILE = 'GoogleDriveFile',
  LINEAR_ISSUE = 'LinearIssue',
  LINEAR_COMMENT = 'LinearComment',
  NOTION_PAGE = 'NotionPage',
  NOTION_DATABASE = 'NotionDatabase',
  JIRA_ISSUE = 'JiraIssue',
  CONFLUENCE_PAGE = 'ConfluencePage',
  GIT_FILE = 'GitFile',
  GIT_CODE = 'GitCode',
  FILE = 'File',
  WEBSITE_PAGE = 'WebsitePage',
  SLACK_DAILY_THREAD = 'SlackDailyThread',
  SLACK_THREAD = 'SlackThread',
  SLACK_THREAD_FROM_ZIP = 'SlackThreadFromZip',
  HUBSPOT_DEAL = 'HubspotDeal',
  BIGQUERY_TABLE = 'BigQueryTable',
  CUSTOM_OBJECT = 'CustomObject',
  INTERCOM_CONVERSATION = 'IntercomConversation',
  MCP_TOOL = 'MCPTool',
  SHAREPOINT_FILE = 'SharepointFile',
  SHAREPOINT_PAGE = 'SharepointPage',
  SALESFORCE = 'Salesforce',
  ZOOM_MEETING_TRANSCRIPT = 'ZoomMeetingTranscript',
  ZENDESK_TICKET = 'ZendeskTicket',
}

export enum SuperSourceAudiencePrefix {
  GITHUB = 'g',
  GITLAB = 'gl',
  GOOGLE_DRIVE = 'gd',
  SLACK_CHANNEL = 'sc',
  LINEAR = 'l',
  NOTION = 'n',
  WEBSITE = 'w',
  SLITE = 's',
  MICROSOFT_SHAREPOINT = 'ms',
  FILE = 'f',
  CONFLUENCE = 'c',
  JIRA = 'j',
  GIT = 'git',
  CUSTOM = 'cu',
  HUBSPOT = 'h',
  ATTIO = 'a',
  ASANA = 'as',
  INTERCOM = 'i',
  BIGQUERY = 'bq',
  MCP = 'mcp',
  SALESFORCE = 'sf',
  ZOOM = 'zm',
  ZENDESK = 'zd',
  MONDAY = 'mo',
  MIRO = 'mi',
  GRANOLA = 'gr',
  AIRTABLE = 'at',
  CLICKUP = 'ck',
  AMPLITUDE = 'am',
  BOX = 'bx',
  SNOWFLAKE = 'sn',
  NOTION_MCP = 'nm',
}

export enum SuperSourceType {
  JIRA_ISSUE = 'jira-issue',
  GITHUB_ISSUE = 'github-issue',
  GITHUB_PR = 'github-pr',
  DRIVE_DOC = 'drive-doc',
  DOCX = 'docx',
  PDF = 'pdf',
  HTML = 'html',
  TXT = 'txt',
  PPTX = 'pptx',
  MARKDOWN = 'markdown',
  SHAREPOINT_PAGE = 'sharepoint-page',
  CONFLUENCE_PAGE = 'confluence-page',
  DRIVE_SLIDE = 'drive-slide',
  SLACK_THREAD = 'slack-thread',
  SLACK_DAILY = 'slack-daily',
  LINEAR_ISSUE = 'linear-issue',
  LINEAR_COMMENT = 'linear-comment',
  NOTION_PAGE = 'notion-page',
  NOTION_DATABASE = 'notion-database',
  NOTION_DATABASE_CHILDREN = 'notion-database-children',
  SLITE_COLLECTION = 'slite-collection',
  SLITE_COLLECTION_CHILDREN = 'slite-collection-children',
  SLITE_NOTE = 'slite-note',
  HUBSPOT_DEAL = 'hubspot-deal',
  ATTIO_NOTE = 'attio-note',
  ATTIO_ENTRY = 'attio-entry',
  ATTIO_RECORD = 'attio-record',
  ATTIO_THREAD = 'attio-thread',
  ATTIO_TRANSCRIPT = 'attio-transcript',
  ASANA_TASK = 'asana-task',
  GIT_CODE = 'git-code',
  // This is deprecated and should not exist in the future anymore
  INTERCOM_CONVERSATION = 'intercom-conversation',
  GITLAB_ISSUE = 'gitlab-issue',
  GITLAB_MR = 'gitlab-merge-request',
  BIGQUERY_TABLE = 'bigquery-table',
  MCP_TOOL = 'mcp-tool',
  SALESFORCE = 'salesforce',
  ZOOM_MEETING_TRANSCRIPT = 'zoom-meeting-transcript',
  ZENDESK_TICKET = 'zendesk-ticket',
}

// not knowledge bases, but still searchable in semantic
export const SUPER_NON_KNOWLEDGE_SOURCES = Object.freeze([
  SuperSource.LINEAR,
  SuperSource.GITHUB,
  SuperSource.JIRA,
  SuperSource.ASANA,
  SuperSource.INTERCOM,
  SuperSource.HUBSPOT,
  SuperSource.ATTIO,
  SuperSource.ZENDESK,
])

// Fragments that describe a source's structure (table schemas, tool
// definitions) rather than its content. Date and author chips exempt these so
// a chip cannot hide a source's registry — see contentSpecificFilter.
export const SUPER_STRUCTURE_FRAGMENT_TYPE_BY_SOURCE: Readonly<
  Partial<Record<SuperSource, SuperSourceType>>
> = Object.freeze({
  [SuperSource.BIGQUERY]: SuperSourceType.BIGQUERY_TABLE,
  [SuperSource.SALESFORCE]: SuperSourceType.SALESFORCE,
  ...Object.fromEntries(
    MCP_TOOL_SUPER_SOURCES.map((source) => [source, SuperSourceType.MCP_TOOL])
  ),
})

// Not semantic content at all, avoid in default filters. Every entry has a
// structure-fragment type above; the reverse is not implied, so the non-MCP
// part of this list stays explicit rather than derived.
export const SUPER_NON_SEMANTIC_SOURCES = Object.freeze([
  SuperSource.BIGQUERY,
  SuperSource.SALESFORCE,
  ...MCP_TOOL_SUPER_SOURCES,
])

export const SUPER_NON_SEMANTIC_SOURCE_TYPES = Object.freeze([
  SuperSourceType.GIT_CODE,
])

export const SUPER_DATABASE_RECORD_SOURCE_TYPES = Object.freeze([
  SuperSourceType.NOTION_DATABASE_CHILDREN,
  SuperSourceType.SLITE_COLLECTION_CHILDREN,
])

// document sources where subTitle represents a section header from markdown chunking
// - we'll encourage the llm towards understanding the chunking structure in these sources
export const SUPER_DOCUMENT_SOURCES_WITH_SECTIONS = Object.freeze([
  SuperSource.SLITE,
  SuperSource.NOTION,
  SuperSource.CONFLUENCE,
  SuperSource.GOOGLE_DRIVE,
  SuperSource.MICROSOFT_SHAREPOINT,
  SuperSource.WEBSITE,
  SuperSource.FILE,
  SuperSource.CUSTOM,
  SuperSource.GIT,
])

type SuperNotionRequiredFields = {
  [K in keyof Pick<SearchObjectSuperNotionFragment, 'icon'>]: NonNullable<
    NonArray<SearchObjectSuperNotionFragment[K]>
  >
}

type SuperSliteRequiredFields = {
  [K in keyof Pick<
    SearchObjectSuperSliteFragment,
    'iconColor' | 'iconShape' | 'reviewState' | 'collectionMetadata'
  >]: NonNullable<NonArray<SearchObjectSuperSliteFragment[K]>>
}

const superSliteRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperSliteRequiredFields>, 'slite'>]: true
} = {
  'slite.iconColor': true,
  'slite.iconShape': true,
  'slite.reviewState': true,
  'slite.collectionMetadata': true,
}

type SuperGithubRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperGithubFragment]: NonNullable<
      NonArray<SearchObjectSuperGithubFragment[K]>
    >
  },
  'labels' | 'assignees' | 'reviewers'
> & {
  [K in keyof Pick<
    SearchObjectSuperGithubFragment,
    'labels' | 'assignees' | 'reviewers'
  >]: NonNullable<NonArray<SearchObjectSuperGithubFragment[K]>>[]
}

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superGithubRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperGithubRequiredFields>, 'github'>]: true
} = {
  'github.mergedBy': true,
  'github.assignees': true,
  'github.closedAt': true,
  'github.closedBy': true,
  'github.draft': true,
  'github.labels': true,
  'github.locked': true,
  'github.mergedAt': true,
  'github.milestone': true,
  'github.number': true,
  'github.owner': true,
  'github.repo': true,
  'github.reviewers': true,
  'github.state': true,
}

type SuperJiraRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperJiraFragment]: NonNullable<
      NonArray<SearchObjectSuperJiraFragment[K]>
    >
  },
  'labels'
> & {
  [K in keyof Pick<SearchObjectSuperJiraFragment, 'labels'>]: NonNullable<
    NonArray<SearchObjectSuperJiraFragment[K]>
  >[]
}

const superJiraRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperJiraRequiredFields>, 'jira'>]: true
} = {
  'jira.assignee': true,
  'jira.creator': true,
  'jira.dueDate': true,
  'jira.key': true,
  'jira.labels': true,
  'jira.percentProgress': true,
  'jira.priority': true,
  'jira.reporter': true,
  'jira.resolution': true,
  'jira.resolutionDate': true,
  'jira.status': true,
  'jira.timeSpent': true,
  'jira.type': true,
}

type SuperLinearRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperLinearFragment]: NonNullable<
      NonArray<SearchObjectSuperLinearFragment[K]>
    >
  },
  'labels'
> & {
  [K in keyof Pick<SearchObjectSuperLinearFragment, 'labels'>]: NonNullable<
    NonArray<SearchObjectSuperLinearFragment[K]>
  >[]
}

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superLinearRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperLinearRequiredFields>, 'linear'>]: true
} = {
  'linear.archivedAt': true,
  'linear.assignee': true,
  'linear.canceledAt': true,
  'linear.completedAt': true,
  'linear.creator': true,
  'linear.cycle': true,
  'linear.cycleNumber': true,
  'linear.estimate': true,
  'linear.identifier': true,
  'linear.labels': true,
  'linear.priority': true,
  'linear.priorityLabel': true,
  'linear.project': true,
  'linear.state': true,
  'linear.stateType': true,
  'linear.team': true,
}

export type SuperRequiredFields = Required<{
  [K in keyof Pick<
    SearchObjectSuperFragment,
    | 'semanticContent'
    | 'title'
    | 'subTitle'
    | 'id'
    | 'breadcrumb'
    | 'index'
    | 'type'
    | 'lastEditedAt'
    | 'url'
    | 'source'
    | 'rootName'
    | 'rootId'
    | 'createdAt'
  >]: NonNullable<NonArray<SearchObjectSuperFragment[K]>>
}> &
  Required<{
    [K in keyof Pick<
      SearchObjectSuperFragment,
      'parentIdList' | 'parentTitleList'
    >]: NonNullable<NonArray<SearchObjectSuperFragment[K]>>[]
  }> & {
    [K in keyof Pick<SearchObjectSuperFragment, 'media'>]: NonNullable<
      NonArray<SearchObjectSuperFragment[K]>
    >
  } & Prefixed<SuperNotionRequiredFields, 'notion'> &
  Prefixed<SuperGithubRequiredFields, 'github'> &
  Prefixed<SuperLinearRequiredFields, 'linear'> &
  Prefixed<SuperJiraRequiredFields, 'jira'> &
  Prefixed<SuperSliteRequiredFields, 'slite'>

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superRequiredFieldsHelper: {
  [K in keyof Required<SuperRequiredFields>]: true
} = {
  ...superGithubRequiredFieldsHelper,
  ...superLinearRequiredFieldsHelper,
  ...superJiraRequiredFieldsHelper,
  ...superSliteRequiredFieldsHelper,
  'notion.icon': true,
  breadcrumb: true,
  id: true,
  index: true,
  lastEditedAt: true,
  media: true,
  parentIdList: true,
  parentTitleList: true,
  semanticContent: true,
  source: true,
  subTitle: true,
  title: true,
  rootId: true,
  type: true,
  url: true,
  rootName: true,
  createdAt: true,
}

export const SUPER_REQUIRED_FIELDS = Object.freeze(
  Object.keys(superRequiredFieldsHelper)
)

export type SuperHit = SearchHitWithRequiredFields<
  SuperRequiredFields & {
    iconShape?: string
    iconColor?: string
    reviewState?: string
    markdown?: string
  }
> & { relativeId: string; mediaRelativeIds?: string[] }

export type SuperToolResultHit = {
  title: string
  dataSource: SuperSource
  source: SuperSourceType
  sourceId: string
  age?: string
  path: string
  summary?: string
  content: string
  /** Fragment position within the document (0-indexed) */
  fragmentIndex?: number
  /** Total fragments in the document (use with fragmentIndex for context, e.g. "3/33") */
  fragmentTotal?: number
  /** The section header this fragment belongs to */
  sectionHeader?: string
  /** The document ID - use with readContext or parents filter */
  documentId?: string
}

export type SuperSourceObject = {
  relativeId: string
  notion?: {
    icon?: string
  }
  slite?: {
    iconColor?: string
    iconShape?: string
    reviewState?: string
  }
  source: SuperSource
  type?: SuperSourceType
  id: string
  index: number
  lastEditedAt: Date | string | null
  media: SuperMedia[]
  parents: { id: string; title: string }[]
  url: string
  explanation?: string
  title: string
}

export const SLITE_PAGE_SOURCE_TYPES: readonly SuperSourceType[] = [
  SuperSourceType.SLITE_NOTE,
  SuperSourceType.SLITE_COLLECTION,
  SuperSourceType.SLITE_COLLECTION_CHILDREN,
]

// A SLITE source can be a native Slite page (navigable in-app by id via the
// note modal / docs route) or an uploaded file (PDF, docx…) that only opens
// through its storage `url` — this distinguishes the former.
export function isSliteNoteOrCollectionSource(
  source: Pick<SuperSourceObject, 'source' | 'type'>
): boolean {
  if (source.source !== SuperSource.SLITE) return false
  return !!source.type && SLITE_PAGE_SOURCE_TYPES.includes(source.type)
}

export enum SuperTools {
  Global = 'Global',
  GitHub = SuperSource.GITHUB,
  GitLab = SuperSource.GITLAB,
  Jira = SuperSource.JIRA,
  Linear = SuperSource.LINEAR,
  Attio = SuperSource.ATTIO,
  Asana = SuperSource.ASANA,
  Intercom = SuperSource.INTERCOM,
  Hubspot = SuperSource.HUBSPOT,
  BigQuery = SuperSource.BIGQUERY,
  Salesforce = SuperSource.SALESFORCE,
}

export type AskMedia = {
  type: string | null
  source: SuperSourceObject | undefined
  mediaUrl: string
  title: string
  generationData?: GenerateImageToolResponse
}

export type SuperStreamEventSources = {
  sources: SuperSourceObject[]
  stepId?: number
} & AgentTaggedEventFields

export type SuperStreamEventSourceExplanation = {
  relativeId: string
  explanation: string
}

export type SuperStreamEventToolExplanationText = {
  text: string
  tool: SuperTools
}

export enum SuperStructuredType {
  Filtered = 'Filtered',
  Buckets = 'Buckets',
  Raw = 'Raw',
  Metric = 'Metric',
}

type FilteredLinear = {
  type: SuperStructuredType.Filtered
  result: (SuperLinearRequiredFields & {
    lastEditedAt: Date | string
    createdAt: Date | string
    title: string
    url: string
  })[]
}

export enum RawChartType {
  Line = 'line',
  Bar = 'bar',
  Pie = 'pie',
  Doughnut = 'doughnut',
  Radar = 'radar',
}

export type ChartArtifact = {
  type: RawChartType
  data: Record<string, estypes.AggregationsAggregate>
  chartConverter: string
}

type Raw = {
  type: SuperStructuredType.Raw
  result: ChartArtifact
}

export type MetricArtifact = {
  value: number
  unit: string
}

type Metric = {
  type: SuperStructuredType.Metric
  result: MetricArtifact
}

export type SuperStreamEventTokenConsumption = {
  input: number
  output: number
  // Totals repeated under `all` (both stream paths send it; per-model rows
  // additionally ride as extra top-level keys — see the TODO in
  // AskResultActionsUI/utils.ts about the misaligned runtime shape).
  all?: { input: number; output: number }
  byModel?: {
    [key: string]: {
      input: number
      output: number
      cacheRead: number
      cacheWrite: number
    }
  }
  cost?: number
  // Raw ledger units (integer, 1 credit = 100 units). Frontend divides by 100.
  creditUnits?: number
  // True when the run's models were overridden (org default profile, dev
  // profile pin, or per-role overrides). Persisted with history so replayed
  // messages keep the non-default-models indicator.
  usesNonDefaultModels?: boolean
}

export type FilteredGithub = {
  type: SuperStructuredType.Filtered
  result: (SuperGithubRequiredFields & {
    lastEditedAt: Date | string
    createdAt: Date | string
    title: string
    url: string
  })[]
}

export type FilteredJira = {
  type: SuperStructuredType.Filtered
  result: (SuperJiraRequiredFields & {
    lastEditedAt: Date
    createdAt: Date
    title: string
    url: string
  })[]
}

export type SuperStreamEventGithub = { esParams?: unknown } & (
  | FilteredGithub
  | Raw
  | Metric
)

type MediaArtifact = SuperMedia

export enum SuperColumnTypes {
  DATE = 'date',
  MULTI_SELECT = 'multi-select',
  NUMBER = 'number',
  TEXT = 'text',
  LINK = 'link',
  TODO = 'todo',
}

type SuperColumnDefinition = {
  type: SuperColumnTypes
  name: string
}

type SuperRecord = (
  | {
      type: SuperColumnTypes.DATE
      value: string
    }
  | { type: SuperColumnTypes.LINK; url: string; title: string }
  | { type: SuperColumnTypes.MULTI_SELECT; options: string[] }
  | { type: SuperColumnTypes.NUMBER; value: number }
  | { type: SuperColumnTypes.TEXT; value: string }
  | { type: SuperColumnTypes.TODO; value: boolean }
)[]

export type TableArtifact = {
  columnDefs: SuperColumnDefinition[]
  records: SuperRecord[]
}

export type OldTableArtifact = FilteredLinear | FilteredJira | FilteredGithub

type ApprovalArtifact = {
  toolCallId: string
}

export enum ArtifactType {
  Media = 'media',
  Chart = 'chart',
  Table = 'table',
  Metric = 'metric',
  OldTable = 'old-table',
  Approval = 'approval',
}

export type SuperStreamEventArtifact = {
  relativeId: string
  artifact:
    | { type: ArtifactType.Media; content: MediaArtifact; tool?: SuperTools }
    | { type: ArtifactType.Chart; content: ChartArtifact; tool?: SuperTools }
    | { type: ArtifactType.Table; content: TableArtifact; tool?: SuperTools }
    | {
        type: ArtifactType.OldTable
        content: OldTableArtifact
        tool?: SuperTools
      }
    | { type: ArtifactType.Metric; content: MetricArtifact; tool?: SuperTools }
    | {
        type: ArtifactType.Approval
        content: ApprovalArtifact
        tool?: SuperTools
      }
}

export type SuperStreamEventArtifacts = {
  artifacts: SuperStreamEventArtifact[]
  stepId?: number
}

export type SuperStreamEventSearchFilters = {
  authors?: { id: string; email: string; displayName: string }[]
  source?: SuperSource
  parents?: SearchParentsHit[]
  range?: {
    lastEditedAt?: {
      gte?: string
      lte?: string
    }
  }
  semanticSearchQueries?: string[]
}

export type SuperStreamEventBuildingAnalysis = {
  tools?: SuperTools[]
  askPrecisions?: string
  instructions?: {
    instructions?: string
    type?: 'simple' | 'map' | 'reduce'
  }
  dateRangeFilter?: {
    gte?: string
    lte?: string
  }
  authorFilters?: string[]
  parentFilters?: string[]
  keywordsSearch?: string[]
  semanticSearch?: string[]
}

export type ESParams = {
  query?: estypes.QueryDslQueryContainer
  aggs?: Record<string, estypes.AggregationsAggregationContainer>
  aggregations?: Record<string, estypes.AggregationsAggregationContainer>
  sort?: estypes.Sort
  size?: number
}

export type SuperAuthorizationErrorPayload = {
  id?: string
  message: string
}

export type SuperAuthorizationSuccessPayload = {
  user: {
    id: string
    hasSuperSeat: boolean | null
  }
  organizationMetadata: {
    superEnabled: boolean | null
  }
  organization: {
    id: string
  }
}

/**
 * Destination for Super output (influnences formatting & citation)
 * - 'web': default -> GFM markdown with :ref[...]{ids=...} citation format
 * - 'slack': Slack mrkdwn with simple {5L1T3-x} citation format
 * - 'api': simple citation format for external API consumers
 */
type SuperDestination = 'web' | 'slack' | 'api'

export type SuperFeatures = {
  useHistory: boolean
  citeSourceInPrompt: boolean
  useArtifactsInAnswer: boolean
  followupClarification: boolean
  explainSources: boolean
  explainCitedSources: boolean
  // when true, Super exposes the `consultSlite` tool
  useSliteAgentDispatch?: boolean
  // strips Slite agent's mutation tools for the run, for assistants mainly
  sliteReadOnly?: boolean
  devReportTokensAndCost?: boolean
  reportCreditCost?: boolean

  unifiedAgent?: boolean
  betaTools?: boolean

  // defaults to 'web'
  destination?: SuperDestination

  // Reserved for V2
  promptCaching?: boolean
}

/** Which agent answers the controller's questions: `ask` is one Slite-only
 *  answer per question on the ask route; the others hold a thread on the
 *  agent route with that primary agent. */
export type AnsweringAgent = 'ask' | 'slite' | 'super'

export type SuperConfig = {
  agent: AnsweringAgent
  onNavigateToSource?: (source: SuperSourceObject) => void
  useReporting: boolean
  /** The surface hosting the controller; runs are attributed to it. */
  via: Via
  initialQuestion?: string
  isRunningInExtension?: boolean
  /** When the run fails with an out-of-credits 402 (which arrives before any
   *  reply streams), surface it as an input-level error above the question
   *  input instead of on the message. Only interactive surfaces that render a
   *  question input (SuperFullpageScreen) can host this; batch surfaces like
   *  Ask-in-Bulk keep the message-level error. */
  surfaceInputLevelErrors?: boolean
  /** Dev flag: offer Super's Simple mode (the ask agent answering the thread). */
  askOnKernel?: boolean
  publicNoteId?: string
  /** The effort, hydrated from and persisted by the host app. */
  initialEffort?: Effort
  onEffortChange?: (effort: Effort) => void
}

export type BasicToolInternalResponse = {
  content: string
  sources?: SuperHit[] | SuperSourceObject[]
}

type BasicToolResponse = {
  content: string
  sources?: SuperToolResultHit[] | SuperSourceObject[]
}

export type ListingToolInternalResponse = {
  tool: SuperTools
  sources: SuperHit[]
  mapping: GenericMapping[]
  mappingName: string
}

type ListingToolResponse = {
  sources: SuperToolResultHit[] | SuperSourceObject[]
}

type AggregationToolResponse = {
  aggregations: Record<string, estypes.AggregationsAggregate>
  sources?: SuperSourceObject[]
}

export type ToolWithBaseTool = {
  tool: GPTFunction
  baseTool: string
}

type DisambiguationToolResponse = {
  tools: ToolWithBaseTool[]
}

type DisambiguationMetadataResponse = {
  disambiguationMetadata: Record<string, unknown>
}

type ChartToolResponse = {
  chartCreated: boolean
  chartStepId: number
  displayInstructions?: string
}

export type GenerateImageToolResponse = {
  artifactsUsed?: string[]
  instruction: string
  imageCreated: boolean
  imageStepId: number
  imageArtifactId?: string
  displayInstructions?: string
}

export type ToolInternalResponse =
  | BasicToolInternalResponse
  | ListingToolInternalResponse
  | AggregationToolResponse
  | ChartToolResponse
  | GenerateImageToolResponse
  | DisambiguationToolResponse
  | DisambiguationMetadataResponse

export type ToolResponse =
  | BasicToolResponse
  | ListingToolResponse
  | AggregationToolResponse
  | ChartToolResponse
  | GenerateImageToolResponse
  | DisambiguationToolResponse
  | DisambiguationMetadataResponse

export function sourceIsSuperHit(
  maybeHit: SuperHit | SuperSourceObject
): maybeHit is SuperHit {
  return '_id' in maybeHit
}

export function sourceListIsSuperHits(
  maybeHits: (SuperHit | SuperSourceObject)[]
): maybeHits is SuperHit[] {
  if (maybeHits.length === 0) return true
  // we only check first; tools will return one type or other
  const first = maybeHits[0]
  return first ? sourceIsSuperHit(first) : false
}

export function isListingToolInternalResponse(
  response: ToolInternalResponse
): response is ListingToolInternalResponse {
  return (
    'sources' in response && 'tool' in response && !('aggregations' in response)
  )
}

export function isBasicToolInternalResponse(
  response: ToolInternalResponse
): response is BasicToolInternalResponse {
  return 'content' in response && !isListingToolInternalResponse(response)
}

export function isToolResponseWithSources(
  response: ToolResponse
): response is { sources: SuperToolResultHit[] } {
  return 'sources' in response && Array.isArray(response.sources)
}

export function isGenerateImageToolResponse(
  response: unknown
): response is GenerateImageToolResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'imageStepId' in response &&
    'instruction' in response
  )
}

export const SuperAssistantWorkflowStepBaseDefinition = z.object({
  // coerce to handle LLMs that return stringified numbers
  stepId: z.coerce
    .number()
    .describe(
      'A unique identifier for this step, used to track execution and results.'
    ),
})

const SuperAssistantWorkflowPlanStepDefinition = z.object({
  ...SuperAssistantWorkflowStepBaseDefinition.shape,

  explanation: z
    .string()
    .describe(
      "Short action name for this step, 50 characters max. Use generic references — avoid baking in specific values (time frames, names, counts) that the user's question may override at runtime."
    ),
  instructions: z
    .string()
    .describe(
      'Additional detail or instructions for the step. Provides context beyond the short explanation.'
    ),
  type: z
    .enum(['single', 'map'])
    .describe(
      'Single for general tool calls that are executed once, map for tool calls that are executed for each item in a list returned by or inferred from a previous step result.'
    ),
  tool: z
    .string()
    .default('')
    .describe(
      'The name of the tool to use for this step. Empty string or omitted for intent-based steps.'
    ),
  parameters: z
    .record(z.string(), z.unknown())
    .default({})
    .describe(
      'Tool parameters as JSON object. May be partial - omit values to be determined at runtime. Must match the tool schema.'
    ),

  // New: optional source type filters for intent-based steps
  sourceTypes: z
    .array(z.nativeEnum(SuperSource))
    .optional()
    .describe(
      'Optional list of source types to restrict this step to (e.g. Slack, Linear). The agent should prefer tools from these sources.'
    ),
  parentIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional list of specific parent/root IDs to restrict this step to (e.g. specific repos, teams, channels).'
    ),
  parentDetails: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        source: z.nativeEnum(SuperSource),
        iconShape: z.string().nullish(),
        iconColor: z.string().nullish(),
      })
    )
    .optional()
    .describe(
      'Display metadata for parent filters. Used for UI rendering only.'
    ),
  iconId: z
    .string()
    .optional()
    .describe(
      'Optional icon identifier for this step. Uses the same key space as getSuperPlanStepIcon: SuperSource values (e.g. "Linear", "GitHub"), tool names (e.g. "semanticSearch", "webSearch", "chartTool"), or special values like "sparkles".'
    ),
})

export const SuperAssistantWorkflowSchema = z.object({
  title: z
    .string()
    .optional()
    .describe(`A short, descriptive title for this workflow (50 characters max).

Use the pattern: [Action/Output] + [Object/Context]

Good examples:
- "Changelog generator"
- "Account summarizer"
- "Support answers assistant"
- "Weekly report builder"

Bad examples:
- "Workflow for creating changelogs" (too verbose)
- "Helper" (too vague)
- "Amazing AI Assistant" (not descriptive)

Keep it simple and focused on what the workflow produces or does.`),

  description: z
    .string()
    .optional()
    .describe(
      `A brief, user-friendly description of what this workflow does. 80 characters max.

Focus on the outcome/benefit, not implementation details.`
    ),
  input: z
    .object({
      type: z.literal('prompt'),

      // Welcome message
      message: z.string().describe(
        `Write a friendly start message for this workflow in markdown.

Keep it casual and concise (2-3 sentences max). Explain what you'll do, then prompt the user with what you need to begin—or if no input is needed, use something like "Ready to get started?"

Don't use headers, dates, or the user's name. Make it breath, add linebreaks if needed, like in a letter.`
      ),

      // Kickstart options
      options: z
        .array(z.string())
        .describe(
          `If relevant, provide 1-3 quick-start buttons that answer the welcome message question with complete information.

Each button must contain ALL inputs needed to run the workflow immediately in one click.

Examples:
- Welcome asks for "time period": ✅ "Last 7 days" ✅ "This month" ✅ "Last quarter"
- Workflow needs no input: ✅ "Let's go" ✅ "Start"

❌ Bad: "Start with candidate name and role" (doesn't provide the actual values)
❌ Bad: "Generate questions" (doesn't answer what the welcome message asked for)
❌ Bad: Do not use real names, but do not use placeholder names that would not work for the user data either.

Only include buttons if you can provide realistic example inputs. If the workflow requires information too specific to pre-fill, leave buttons empty.`
        )
        .optional(),
    })
    .describe(
      'This defines the "input" of the workflow, i.e. how it starts and what is the initial message to the user.'
    )
    .optional(),

  steps: z.array(SuperAssistantWorkflowPlanStepDefinition),

  settings: z
    .object({
      webSearch: z.boolean().optional(),
    })
    .optional()
    .describe('Workflow-level settings persisted alongside the plan.'),

  outputGuidance: z
    .string()
    .optional()
    .describe(
      'Optional guidance for the final output format/structure. Tells the agent how to present results.'
    ),
})

export type SuperAssistantWorkflow = z.infer<
  typeof SuperAssistantWorkflowSchema
>

export function isSuperAssistantWorkflow(
  workflow: unknown
): workflow is SuperAssistantWorkflow {
  return SuperAssistantWorkflowSchema.safeParse(workflow).success
}

export type SuperStreamEventFailingTool = {
  tool: SuperTools
  error?: {
    message?: string
    id?: string
  }
}

/** Identifies which agent emitted an event in a multi-agent (consult) session. */
export type AgentId = 'slite' | 'super'

/**
 * Markers the server injects onto events in a multi-agent (consult)
 * session so the client can attribute and nest them:
 * - `__agent`: the agent that emitted the event.
 * - `parentStepId`: subagent events only — the primary-plan stepId whose
 *   consult spawned this subagent run, used to nest its steps under the
 *   right primary step regardless of parallel tool calls.
 * Both are absent in single-agent flows (backward-compatible wire format).
 */
export type AgentTaggedEventFields = {
  __agent?: AgentId
  parentStepId?: number
}

/** Who did this, stamped server-side from the caller's session. */
export type EventActor = {
  id: string
  displayName: string
  photoURL?: string | null
}

export type SuperStreamToolParameters = {
  /** Step ID - used by both split and unified agents */
  stepId?: number
  text: string
} & AgentTaggedEventFields

export enum SuperToolChainEventId {
  TEXT = 'text',
  TOOL_CALL = 'tool-call',
  TOOL_RESULT = 'tool-result',
  TOOL_ERROR = 'tool-error',
  PLAN_RESULT = 'plan-result',
  PLAN_UPDATE = 'plan-update',
  /** Slite Agent: requests user approval before executing a mutation */
  APPROVAL_REQUEST = 'approval-request',
  /** Slite Agent: outcome of a previously-requested approval (approved, rejected, or errored) */
  APPROVAL_RESULT = 'approval-result',
  AGENT_REPORT = 'agent-report',
  /** The agent's answer on a thread, posted at the end of the round it ran. */
  AGENT_REPLY = 'agent-reply',
  /** A participant's message on the thread. Opens no agent round by itself. */
  THREAD_MESSAGE = 'thread-message',
  /** Someone deleted a message; its text is already gone from the log. */
  THREAD_MESSAGE_DELETED = 'thread-message-deleted',
}

/**
 * Task status for unified agent's dynamic plan
 */
export type UnifiedAgentPlanTaskStatus = 'pending' | 'in_progress' | 'completed'

/**
 * A single task in the unified agent's dynamic plan
 * Uses numeric stepId to align with split agent's side effects system
 */
export type UnifiedAgentPlanTask = {
  /** Numeric step ID - aligns with split agent's stepId for side effects */
  stepId: number
  description: string
  status: UnifiedAgentPlanTaskStatus
  /** Tool name - set when tool execution begins (enables icon display) */
  tool?: string
  /** Icon identifier for steps without a specific tool (e.g. SuperSource name, 'sparkles', 'webSearch') */
  iconId?: string
  /** Link to actual tool call */
  toolCallId?: string
  /** Parameter description from side effects (latest invocation) */
  details?: string
  /** Full history of parameter descriptions (for re-run stacking) */
  detailsHistory?: string[]
  /** Sources from tool results */
  sources?: SuperSourceObject[]
}

/**
 * The unified agent's dynamic plan
 */
export type UnifiedAgentPlan = {
  tasks: UnifiedAgentPlanTask[]
}

type SuperToolChainEventText = {
  id: SuperToolChainEventId.TEXT
  event: {
    text: string
    source: string
  }
}

export type SuperToolChainEventToolCall = {
  id: SuperToolChainEventId.TOOL_CALL
  event: {
    tool: string
    parameters: Record<string, unknown> & {
      /** Step ID - used by both split and unified agents */
      stepId?: number
      baseTool?: string
    }
    toolCallId: string
  } & AgentTaggedEventFields
}

export type SuperToolChainEventToolResult = {
  id: SuperToolChainEventId.TOOL_RESULT
  event: {
    tool: string
    toolCallId: string
    result: Omit<ToolResponse, 'sources'> & { sources?: SuperSourceObject[] }
    content?: string
  } & AgentTaggedEventFields
}

type SuperToolChainEventToolError = {
  id: SuperToolChainEventId.TOOL_ERROR
  event: {
    tool: string
    toolCallId: string
    error: string
  }
}

export type SuperToolChainEventPlanResult = {
  id: SuperToolChainEventId.PLAN_RESULT
  event: SuperAssistantWorkflow
}

type SuperToolChainEventPlanUpdate = {
  id: SuperToolChainEventId.PLAN_UPDATE
  event: UnifiedAgentPlan & {
    /**
     * Slite Agent only: stepId boundary for the current turn. Tasks with
     * stepId >= this value were introduced this turn. Use this
     * to render only the slice of the plan relevant to current message
     */
    turnStartStepId?: number
    /**
     * Slite Agent only: stepIds whose status, description, or membership
     * changed in this specific update call; use this to surface
     * pre-existing tasks whose status flips during a continuation turn
     */
    changedStepIds?: number[]
    /** Slite Agent only: this update discarded the prior plan. */
    replaced?: boolean
  } & AgentTaggedEventFields
}

/** An `insert` with neither anchor appends at the end of the doc; a
 *  `modifyRange` with empty `sliteml` deletes the range. */
export type ApprovalEdit =
  | {
      op: 'insert'
      afterBlockId?: string
      beforeBlockId?: string
      sliteml: string
    }
  | { op: 'replace'; sliteml: string }
  | {
      op: 'modifyRange'
      startBlockId: string
      endBlockId: string
      sliteml: string
    }
  | { op: 'remove'; blockIds: string[] }

export type CollectionCell = {
  column: string
  value: string
  columnType?: string
}

/** One change inside a collection or in-document table. Column and row ids
 *  arrive already resolved to names; the client formats the values. */
export type CollectionChange =
  | { kind: 'addColumn'; column: string; columnType: string }
  | { kind: 'removeColumn'; column: string }
  | { kind: 'setCells'; row?: string; cells: CollectionCell[] }
  | { kind: 'addRow'; cells: CollectionCell[] }
  | { kind: 'removeRow'; row: string }

/** Ops on a doc-body table, replayed by the client to preview it in the doc. */
export type TableEdit = {
  tableBlockId: string
  columns?: { name: string; type: string }[]
  removeColumns?: string[]
  setCells?: { recordKey: string; columnKey: string; value: string }[]
  addRows?: { fields?: Record<string, string> }[]
  removeRows?: string[]
}

/** The `kind` discriminants of ApprovalCard. String-enum members satisfy the
 *  union's inline literals, so producers may write either form. */
export enum ApprovalCardKind {
  DocEdit = 'docEdit',
  CollectionEdit = 'collectionEdit',
  Organize = 'organize',
  ReviewState = 'reviewState',
  Metadata = 'metadata',
}

/** What the client draws for one approval. A card names the rendering only —
 *  several writes share a card, and one write picks between cards by payload. */
export type ApprovalCard =
  | {
      kind: 'docEdit'
      noteId: string
      noteTitle?: string
      parentNoteId?: string
      isCreate?: boolean
      edits: ApprovalEdit[]
    }
  | {
      kind: 'collectionEdit'
      noteId: string
      noteTitle?: string
      /** What the change is about — picks the card's header. */
      subject: 'collection' | 'table' | 'row'
      isCreate?: boolean
      changes: CollectionChange[]
      tableEdit?: TableEdit
      /** A collection row is a doc, so a row write can also replace its body. */
      edits?: ApprovalEdit[]
    }
  | {
      kind: 'organize'
      action: 'move' | 'archive' | 'restore' | 'changeOwner'
      noteId: string
      parentNoteId?: string
      ownerId?: string
      ownerGroupId?: string
    }
  | {
      kind: 'reviewState'
      action: 'verify' | 'flagOutdated' | 'requestVerification' | 'clear'
      noteId: string
      until?: string
      forMonths?: number
      message?: string
      /** A verification request addressed to a new owner, who takes the doc. */
      ownerId?: string
    }
  | {
      kind: 'metadata'
      noteId: string
      title?: string
      /** null clears the icon; absent leaves it alone. */
      iconShape?: string | null
      iconColor?: string | null
      contentWidth?: 'regular' | 'wide'
    }

type ApprovalRequestFields = {
  toolCallId: string
  /** Artifact id of the card, referenced by the answer's `<approval>` tag. */
  relativeId?: string
  /** Server-generated, tech-flavored, e.g. "Modify blocks A to B in note X". */
  description: string
  /** The agent's plain-language summary; frontends prefer it over `description`. */
  explanation?: string
  /** If true, the agent has no more work after this action */
  isFinalAction?: boolean
  /** Document version at the moment the agent read the doc. */
  documentVersion?: number
}

/** The agent kernel's approval request: the card is the whole render contract. */
type ApprovalCardRequest = ApprovalRequestFields & { card: ApprovalCard }

/** The graph route's approval request: the client draws the card from the raw
 *  write and echoes `execution` back to run it after approval. */
type SignedApprovalRequest = ApprovalRequestFields & {
  tool: string
  action: string
  noteId: string
  parentNoteId?: string
  noteTitle?: string
  blockRange?: { startBlockId: string; endBlockId: string }
  proposedContent?: string
  changeLabels?: {
    columnLabels?: Record<string, string>
    recordLabels?: Record<string, string>
  }
  execution: {
    toolName: string
    toolInput: Record<string, unknown>
    signature: string
  }
}

/**
 * Slite Agent approval request — sent when the agent wants to perform
 * a mutation and needs user confirmation.
 */
export type SliteAgentApprovalRequest = {
  id: SuperToolChainEventId.APPROVAL_REQUEST
  event: (ApprovalCardRequest | SignedApprovalRequest) & AgentTaggedEventFields
}

export enum ApprovalOutcome {
  Approved = 'approved',
  Rejected = 'rejected',
  Errored = 'errored',
  /** The agent took back a card nobody had decided on yet. */
  Withdrawn = 'withdrawn',
}

// reflects the outcome of the approval
type SliteAgentApprovalResult = {
  id: SuperToolChainEventId.APPROVAL_RESULT
  event: {
    // for aligning with correct internal llm calls
    toolCallId: string
    /** Card id — pairs the result to its request card even when fan-out
     *  siblings share a toolCallId (kernel route). */
    relativeId?: string
    outcome: ApprovalOutcome
    // result content when approved
    content?: string
    error?: string
    // rejeection text if given
    feedback?: string
    resolvedAt: string
    /** Caller attribution when recorded; older results may omit it. */
    actor?: EventActor
  } & AgentTaggedEventFields
}

export type AgentReportChange = {
  id: string
  explanation: string
}

export type AgentReport = {
  title: string
  summary: string
  changes: AgentReportChange[]
}

type SuperToolChainEventAgentReport = {
  id: SuperToolChainEventId.AGENT_REPORT
  event: AgentReport & AgentTaggedEventFields
}

export type AgentReply = {
  messageId: string
  /** Markdown with citation markers, resolved by the thread's sources. */
  text: string
  createdAt: string
}

type SuperToolChainEventAgentReply = {
  id: SuperToolChainEventId.AGENT_REPLY
  event: AgentReply & AgentTaggedEventFields
}

export type ThreadMessageMention =
  | { kind: 'user'; userId: string }
  /** The agent has no user ID. */
  | { kind: 'agent' }

export type ThreadMessage = {
  messageId: string
  text: string
  content?: unknown[]
  author: EventActor
  mentions: ThreadMessageMention[]
  createdAt: string
}

type ThreadMessageDeletion = {
  messageId: string
  actor: EventActor
  deletedAt: string
}

type SuperToolChainEventThreadMessageDeleted = {
  id: SuperToolChainEventId.THREAD_MESSAGE_DELETED
  event: ThreadMessageDeletion & AgentTaggedEventFields
}

type SuperToolChainEventThreadMessage = {
  id: SuperToolChainEventId.THREAD_MESSAGE
  event: ThreadMessage & AgentTaggedEventFields
}

/** Revision used to detect events appended since the client loaded the thread. */
export const threadRevision = (events: readonly unknown[]): string =>
  String(events.length)

export type SuperToolChainEvent =
  | SuperToolChainEventText
  | SuperToolChainEventToolCall
  | SuperToolChainEventToolResult
  | SuperToolChainEventToolError
  | SuperToolChainEventPlanResult
  | SuperToolChainEventPlanUpdate
  | SliteAgentApprovalRequest
  | SliteAgentApprovalResult
  | SuperToolChainEventAgentReport
  | SuperToolChainEventAgentReply
  | SuperToolChainEventThreadMessageDeleted
  | SuperToolChainEventThreadMessage

export function isSupportedSuperToolChainEventId(
  askEventId: string | undefined
): askEventId is SuperToolChainEventId {
  return Boolean(
    askEventId &&
      Object.values(SuperToolChainEventId)
        .map((a) => a.toString())
        .includes(askEventId)
  )
}

export enum SuperQueryOrigin {
  API = 'api',
  SLACK = 'slack',
  WEB = 'web',
  EXTENSION = 'extension',
  MCP = 'mcp',
  MOBILE = 'mobile',
  AUTOMATION = 'automation',
}

const TokenUsageSchema = z.object({
  input: z.number(),
  output: z.number(),
  cacheRead: z.number().optional(),
  cacheWrite: z.number().optional(),
})

const TokensUsageByModelSchema = z.record(z.string(), TokenUsageSchema)

export const CreateSuperQueryParamsSchema = z.object({
  organizationId: z.string(),
  userId: z.string().nullable().optional(),
  assistantId: z.string().nullable().optional(),
  origin: z.nativeEnum(SuperQueryOrigin),
  superHistoryId: z.string().nullable().optional(),
  tokensUsage: TokensUsageByModelSchema.nullable().optional(),
  durationMs: z.number().nullable().optional(),
  success: z.boolean().optional().default(true),
})

export type CreateSuperQueryParams = z.infer<
  typeof CreateSuperQueryParamsSchema
>
